import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { organizationService } from '@services/organization';
import { useTranslation } from '@hooks/useTranslation';

export interface CreateOrganizationWithTrialPayload {
  organizationName: string;
  showAsSubcontractor?: boolean;
}

/**
 * Creates an additional organization on a card-free trial.
 *
 * The backend enforces the free-trial cap, so a rejection here is expected rather than
 * exceptional — the message it returns already explains the cap and how to free a slot,
 * which is why it is surfaced verbatim.
 */
export const useCreateOrganizationWithTrial = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['organizations', 'create-with-trial'],
    mutationFn: (payload: CreateOrganizationWithTrialPayload) =>
      organizationService.createOrganizationWithTrial(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      queryClient.invalidateQueries({ queryKey: ['user-organizations'] });
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({
        queryKey: ['freeTrialOrganizationUsage'],
      });

      toast.success(tActionMessages.organizationCreatedTrialStarts);

      // Reload so the app picks up the new default organization.
      setTimeout(() => {
        window.location.href = '/';
      }, 1500);
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.errors?.[0]?.message ||
          error?.message ||
          tActionMessages.couldNotCreateOrganization,
      );
    },
  });
};

export default useCreateOrganizationWithTrial;
